import { knownMyUrls } from '@sola/my-sdk/constants.ts';
import type {
  GraphQLCommitment,
  GraphQLCommitmentParticipant,
  GraphQLPledge,
} from '@sola/schema/dotcom/supporters/schema.generated.ts';
import { compareUpcomingBirthday } from '@sola/shared-utils/children/utils.ts';
import {
  filterCurrentCommitments,
  filterCurrentSponsorshipCommitments,
  isSponsorshipCommitment,
} from '@sola/shared-utils/supporters/commitments.ts';
import { useSupporterQuery } from '@sola/supporter-sdk/hooks/base.ts';
import {
  commitmentsQuery,
  departureSummaryQuery,
  givingTotalsQuery,
  paymentMethodsQuery,
  pledgesQuery,
  recentGivingQuery,
  recentLettersQuery,
} from '@sola/supporter-sdk/queries.ts';
import { filterRecentPledges } from '@sola/supporter-sdk/utils/pledges.ts';

import type { PrimaryCtaResult } from '../../utils/home/primary-cta-decision.ts';
import { resolvePrimaryCta } from '../../utils/home/primary-cta-decision.ts';
import type { SecondaryCtaResolution } from '../../utils/home/secondary-cta-decision.ts';
import { resolveSecondaryCtas } from '../../utils/home/secondary-cta-decision.ts';

export enum HomeOverviewVariant {
  Loading = 'loading',
  Empty = 'empty',
  List = 'list',
  Gallery = 'gallery',
}
export enum HomeOverviewType {
  Commitment = 'commitment',
  Pledge = 'pledge',
}

export type HomeOverviewCommitmentItem = {
  type: HomeOverviewType.Commitment;
  commitment: GraphQLCommitment;
  participant: GraphQLCommitmentParticipant | null;
};

export type HomeOverviewPledgeItem = {
  type: HomeOverviewType.Pledge;
  pledge: GraphQLPledge;
};

export type HomeOverviewItem =
  | HomeOverviewCommitmentItem
  | HomeOverviewPledgeItem;

export type HomeOverviewMoreDestination = 'sponsorships' | 'commitments';

export type HomeOverviewData = {
  variant: HomeOverviewVariant;
  items: HomeOverviewItem[];
  showMoreTile: boolean;
  moreHref: string | null;
  showCtas: boolean;
  primaryCtas: PrimaryCtaResult[];
  secondaryCtas: SecondaryCtaResolution;
  isError?: boolean;
};

// Maximum number of tiles shown in the gallery, including the "More" tile.
const GALLERY_MAX_TILES = 5;

const buildItems = (
  commitments: GraphQLCommitment[],
  pledges: GraphQLPledge[],
): HomeOverviewItem[] => {
  const sponsorships = filterCurrentSponsorshipCommitments(commitments);

  const commitmentItems: HomeOverviewCommitmentItem[] = sponsorships.map(
    (commitment) => {
      const participant = commitment.participant ?? null;

      return {
        type: HomeOverviewType.Commitment,
        commitment,
        participant,
      };
    },
  );

  const pledgeItems: HomeOverviewPledgeItem[] = filterRecentPledges(
    pledges,
  ).map((pledge) => ({
    type: HomeOverviewType.Pledge,
    pledge,
  }));

  return [...commitmentItems, ...pledgeItems];
};

export const sortHomeOverviewItems = (
  items: HomeOverviewItem[],
): HomeOverviewItem[] => {
  const commitments = items.filter(
    (item): item is HomeOverviewCommitmentItem =>
      item.type === HomeOverviewType.Commitment,
  );
  const pledges = items.filter(
    (item): item is HomeOverviewPledgeItem =>
      item.type === HomeOverviewType.Pledge,
  );

  // Sort sponsorships by their next birthday, with missing or invalid dates last.
  const sortedCommitments = commitments.toSorted((a, b) => {
    return compareUpcomingBirthday(
      a.participant?.birthdate,
      b.participant?.birthdate,
    );
  });

  // The API guarantees YYYY-MM-DD dates, so lexical order is chronological.
  // Sort pledges: nextInstallmentDate ascending, null/undefined dates last.
  const sortedPledges = pledges.toSorted((a, b) => {
    const dateA = a.pledge.nextInstallmentDate;
    const dateB = b.pledge.nextInstallmentDate;

    if (!dateA && !dateB) {
      return 0;
    }
    if (!dateA) {
      return 1;
    }
    if (!dateB) {
      return -1;
    }
    return dateA.localeCompare(dateB);
  });

  return [...sortedCommitments, ...sortedPledges];
};

const determineVariant = (contentCount: number): HomeOverviewVariant => {
  if (contentCount <= 0) {
    return HomeOverviewVariant.Empty;
  }
  if (contentCount <= 2) {
    return HomeOverviewVariant.List;
  }
  return HomeOverviewVariant.Gallery;
};

export const resolveMoreDestination = (
  commitments: GraphQLCommitment[],
  pledges: GraphQLPledge[],
  visibleItems: HomeOverviewItem[],
): HomeOverviewMoreDestination => {
  const visibleCommitmentIds = new Set(
    visibleItems
      .filter(
        (item): item is HomeOverviewCommitmentItem =>
          item.type === HomeOverviewType.Commitment,
      )
      .map((item) => item.commitment.internalId),
  );
  const visiblePledgeIds = new Set(
    visibleItems
      .filter(
        (item): item is HomeOverviewPledgeItem =>
          item.type === HomeOverviewType.Pledge,
      )
      .map((item) => item.pledge.pledgeId),
  );

  const remainingCommitments = filterCurrentCommitments(commitments).filter(
    (commitment) => !visibleCommitmentIds.has(commitment.internalId),
  );
  const remainingPledges = filterRecentPledges(pledges).filter(
    (pledge) => !visiblePledgeIds.has(pledge.pledgeId),
  );

  return remainingPledges.length === 0 &&
    remainingCommitments.every(isSponsorshipCommitment)
    ? 'sponsorships'
    : 'commitments';
};

export const resolveMoreHref = (
  commitments: GraphQLCommitment[],
  pledges: GraphQLPledge[],
  visibleItems: HomeOverviewItem[],
): string => {
  const destination = resolveMoreDestination(
    commitments,
    pledges,
    visibleItems,
  );

  return destination === 'sponsorships'
    ? knownMyUrls.sponsorshipsBase
    : knownMyUrls.billingCommitments;
};

const EMPTY_CTAS = {
  primaryCtas: [] as PrimaryCtaResult[],
  secondaryCtas: [] as SecondaryCtaResolution,
};

export const useHomeOverview = (
  hasNoSponsorshipsContent: boolean,
): HomeOverviewData => {
  // Resolve gallery data independently so slower CTA-only activity queries do
  // not block the supporter’s commitments from rendering.
  const { data, isPending, isError } = useSupporterQuery([
    commitmentsQuery,
    givingTotalsQuery,
    departureSummaryQuery,
    pledgesQuery,
    paymentMethodsQuery,
  ]);

  // Not worrying if the recent queries fail - if they do, pending should clear
  // and we'll deliver the CTAs that we can (which could of course mean the BG
  // appeal instead of thank you)
  const { data: recentLetterAndGivingData, isPending: isRecentPending } =
    useSupporterQuery([recentLettersQuery, recentGivingQuery]);

  if (isPending) {
    return {
      variant: HomeOverviewVariant.Loading,
      items: [],
      showMoreTile: false,
      moreHref: null,
      showCtas: false,
      ...EMPTY_CTAS,
    };
  }

  if (isError || !data) {
    return {
      variant: HomeOverviewVariant.Empty,
      items: [],
      showMoreTile: false,
      moreHref: null,
      isError,
      showCtas: false,
      ...EMPTY_CTAS,
    };
  }

  const [
    rawCommitments,
    givingTotals,
    departureSummary,
    rawPledges,
    rawPaymentMethods,
  ] = data;

  const commitments: GraphQLCommitment[] = rawCommitments ?? [];
  const pledges: GraphQLPledge[] = rawPledges ?? [];
  const paymentMethods = rawPaymentMethods ?? [];

  const items = buildItems(commitments, pledges);
  const sortedItems = sortHomeOverviewItems(items);
  const variant = determineVariant(sortedItems.length);

  const itemsForDisplay = sortedItems;

  const showMoreTile = itemsForDisplay.length > GALLERY_MAX_TILES;
  let visibleItems = itemsForDisplay;
  if (showMoreTile) {
    // Only one active pledge right now, so reserve one tile
    // and place it after the others.
    const pledge = itemsForDisplay.find(
      (item) => item.type === HomeOverviewType.Pledge,
    );
    if (pledge) {
      const galleryCommitments = itemsForDisplay.filter(
        (item) => item.type === HomeOverviewType.Commitment,
      );
      visibleItems = [
        ...galleryCommitments.slice(0, GALLERY_MAX_TILES - 2),
        pledge,
      ];
    } else {
      visibleItems = itemsForDisplay.slice(0, GALLERY_MAX_TILES - 1);
    }
  }

  const moreHref = showMoreTile
    ? resolveMoreHref(commitments, pledges, visibleItems)
    : null;

  if (isRecentPending) {
    return {
      variant,
      items: visibleItems,
      showMoreTile,
      moreHref,
      showCtas: false,
      ...EMPTY_CTAS,
    };
  }

  const [rawRecentLetters, recentGiving] = recentLetterAndGivingData;
  const { lettersAvailable = [] } = rawRecentLetters ?? {};

  // Primary first — the secondary engine needs the resolved primary CTAs to
  // prevent duplicate asks and apply campaign-specific suppression rules.
  const primaryCtas = resolvePrimaryCta({
    commitments,
    pledges,
    givingTotals,
    departureSummary,
    lettersAvailable,
    recentGiving,
    hasNoSponsorshipsContent,
  });

  const secondaryCtas = resolveSecondaryCtas({
    commitments,
    pledges,
    paymentMethods,
    primaryCtas,
  });

  return {
    variant,
    items: visibleItems,
    showMoreTile,
    moreHref,
    showCtas: true,
    primaryCtas,
    secondaryCtas,
  };
};
